import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/supabaseClient';
import { ArrowLeft, ArrowRight, Loader2, MapPin, Building2, User, FileText, Files, Palmtree, ShieldCheck, Mail, Clock, Plus, Edit2, X, Save, PauseCircle, PlayCircle, XCircle, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { hasCap } from '@/lib/capabilities';
import { useEffectiveMe } from '@/lib/effectiveMe';
import DocumentsTab from '@/components/DocumentsTab';
import AssignmentScopeSelector from '@/components/AssignmentScopeSelector';
import AccessoTab from '@/components/AccessoTab';

const TABS = [
  { id: 'identita', label: 'Identità', icon: User },
  { id: 'lavoro', label: 'Lavoro', icon: Building2 },
  { id: 'contratto', label: 'Contratto', icon: FileText },
  { id: 'documenti', label: 'Documenti', icon: Files },
  { id: 'ferie', label: 'Ferie', icon: Palmtree },
  { id: 'accesso', label: 'Accesso', icon: ShieldCheck },
  { id: 'timeline', label: 'Timeline', icon: Clock },
];

const LIFECYCLE_LABEL = { BOZZA: 'Bozza', ATTIVO: 'Attivo', SOSPESO: 'Sospeso', IN_USCITA: 'In uscita', CESSATO: 'Cessato', ARCHIVIATO: 'Archiviato' };
const LIFECYCLE_TONE = {
  BOZZA: 'border-stone-200 bg-stone-50 text-stone-600',
  ATTIVO: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  SOSPESO: 'border-amber-200 bg-amber-50 text-amber-800',
  IN_USCITA: 'border-amber-200 bg-amber-50 text-amber-800',
  CESSATO: 'border-red-200 bg-red-50 text-red-700',
  ARCHIVIATO: 'border-stone-200 bg-stone-100 text-stone-600',
};

const POLICY_SOURCE_OPTIONS = [
  ['CCNL', 'CCNL'],
  ['CONTRATTO_INDIVIDUALE', 'Contratto individuale'],
  ['ACCORDO_INDIVIDUALE', 'Accordo individuale'],
];

const newPolicyDraft = (policy) => ({
  nome_configurazione: policy?.nomeConfigurazione ? `${policy.nomeConfigurazione} · nuova versione` : 'Policy individuale ferie',
  fonte: policy?.fonte || '',
  tipo_fonte: policy?.tipoFonte && policy.tipoFonte !== 'LEGACY' ? policy.tipoFonte : 'CONTRATTO_INDIVIDUALE',
  ccnl: policy?.ccnl || '',
  codice_ccnl: policy?.codiceCcnl || '',
  giorni_annui: policy?.giorniAnnui || 30,
  criterio_conteggio: policy?.criterioConteggio || '5_GIORNI',
  settimana_lavorativa: policy?.settimanaLavorativa || 5,
  data_inizio_validita: new Date().toISOString().slice(0, 10),
});

export default function PersonaScheda() {
  const { id } = useParams();
  const navigate = useNavigate();
  const me = useEffectiveMe();
  const canEdit = hasCap(me, 'personale.edit');
  const canAssign = hasCap(me, 'personale.assignments.manage');
  const canManagePolicy = hasCap(me, 'ferie.config.manage');
  // The API additionally restricts lifecycle transitions to these roles; keep
  // the same visible boundary while using effective capabilities for the UX.
  const canLifecycle = hasCap(me, 'personale.edit')
    && me?.role_codes?.some((r) => ['CEO', 'TITOLARE', 'RESP_GEN'].includes(r));

  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => {
    if (typeof window !== 'undefined') {
      const sp = new URLSearchParams(window.location.search).get('tab');
      const allowed = ['identita','lavoro','contratto','documenti','ferie','accesso','timeline'];
      if (sp && allowed.includes(sp)) return sp;
    }
    return 'identita';
  });
  const [modal, setModal] = useState(null); // {kind, data}
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [policyEditorOpen, setPolicyEditorOpen] = useState(false);
  const [policyDraft, setPolicyDraft] = useState(() => newPolicyDraft(null));
  const [policySaving, setPolicySaving] = useState(false);
  const mountedRef = useRef(false);
  const loadVersionRef = useRef(0);

  const load = async () => {
    const loadVersion = ++loadVersionRef.current;
    try {
      const data = await apiFetch(`/api/personale/${id}`);
      if (mountedRef.current && loadVersion === loadVersionRef.current) {
        setP(data);
      }
    } catch (e) {
      if (!mountedRef.current || loadVersion !== loadVersionRef.current) return;
      if (e.status === 403) { toast.error('Fuori scope'); navigate('/personale'); return; }
      toast.error(e.message || 'Errore caricamento');
    } finally {
      if (mountedRef.current && loadVersion === loadVersionRef.current) {
        setLoading(false);
      }
    }
  };

  const saveEmployeePolicy = async (event) => {
    event.preventDefault();
    setPolicySaving(true);
    try {
      await apiFetch('/api/ferie/policy', {
        method: 'POST',
        body: JSON.stringify({
          ...policyDraft,
          employee_id: p.id,
          giorni_annui: Number(policyDraft.giorni_annui),
          settimana_lavorativa: Number(policyDraft.settimana_lavorativa),
          regola_maturazione: { tipo: 'mensile' },
          regola_riporto: {},
          regola_scadenza: {},
        }),
      });
      toast.success('Policy individuale salvata');
      setPolicyEditorOpen(false);
      await load();
    } catch (error) {
      toast.error(error.message || 'Impossibile salvare la policy individuale');
    } finally {
      setPolicySaving(false);
    }
  };

  const deactivateEmployeeOverride = async () => {
    const reason = window.prompt('Motivo della disattivazione (facoltativo):', '');
    if (reason === null) return;
    setPolicySaving(true);
    try {
      await apiFetch(`/api/ferie/policy/employee/${p.id}/override`, {
        method: 'PATCH',
        body: JSON.stringify({ reason: reason.trim() || null }),
      });
      toast.success('Override individuale disattivato');
      await load();
    } catch (error) {
      toast.error(error.message || 'Impossibile disattivare l’override individuale');
    } finally {
      setPolicySaving(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    load();
    return () => {
      mountedRef.current = false;
      loadVersionRef.current += 1;
    };
    // The selected person is the lifecycle boundary for the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const doLifecycle = async (action, reason) => {
    if (lifecycleBusy) return;
    setLifecycleBusy(true);
    try {
      await apiFetch(`/api/personale/${id}/lifecycle`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reason }),
      });
      toast.success(`Stato aggiornato: ${action}`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setLifecycleBusy(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-stone-500"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;
  if (!p) return null;

  const F = ({ label, value, testid }) => (
    <div>
      <div className="text-xs uppercase tracking-wider text-stone-400 font-semibold">{label}</div>
      <div className="mt-1 text-sm text-brand-navy" data-testid={testid}>{value || <span className="text-stone-400">—</span>}</div>
    </div>
  );

  const lifecycle = p.lavoro.lifecycle_status || 'ATTIVO';
  const showActionsMenu = canLifecycle && p.legacy_id !== 'd6';

  return (
    <div className="space-y-6" data-testid="persona-scheda">
      <div>
        <Link to="/personale" className="mb-2 inline-flex min-h-10 items-center gap-1 rounded-lg px-1 text-xs text-stone-500 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200" data-testid="back-to-personale">
          <ArrowLeft className="w-3 h-3" /> Personale
        </Link>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green mb-1">Scheda persona</div>
            <h1 className="break-words font-display text-3xl font-semibold text-brand-navy md:text-4xl" data-testid="persona-name">{p.identity.display_name}</h1>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-stone-500">
              {p.identity.codice_ros} · {p.lavoro.company_name || '—'}
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-semibold uppercase ${LIFECYCLE_TONE[lifecycle] || LIFECYCLE_TONE.BOZZA}`}
                data-testid="persona-lifecycle">{LIFECYCLE_LABEL[lifecycle] || lifecycle}</span>
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:justify-end">
            {showActionsMenu && lifecycle === 'ATTIVO' && (
              <Button variant="outline" disabled={lifecycleBusy} className="w-full gap-1 border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50 sm:w-auto" onClick={() => doLifecycle('SUSPEND')} data-testid="btn-suspend">
                {lifecycleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PauseCircle className="w-4 h-4" />} Sospendi
              </Button>
            )}
            {showActionsMenu && lifecycle === 'SOSPESO' && (
              <Button variant="outline" disabled={lifecycleBusy} className="w-full gap-1 border-brand-green text-brand-green hover:bg-brand-green hover:text-white disabled:opacity-50 sm:w-auto" onClick={() => doLifecycle('REACTIVATE')} data-testid="btn-reactivate">
                {lifecycleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Riattiva
              </Button>
            )}
            {showActionsMenu && ['ATTIVO', 'SOSPESO'].includes(lifecycle) && (
              <Button variant="outline" disabled={lifecycleBusy} className="w-full gap-1 border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 sm:w-auto" onClick={() => window.confirm('Cessare rapporto? Tutti gli assignments verranno chiusi con data odierna.') && doLifecycle('CESSATE')} data-testid="btn-cessate">
                {lifecycleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Cessa
              </Button>
            )}
            {showActionsMenu && ['CESSATO', 'SOSPESO'].includes(lifecycle) && (
              <Button variant="outline" disabled={lifecycleBusy} className="w-full gap-1 text-stone-600 disabled:opacity-50 sm:w-auto" onClick={() => doLifecycle('ARCHIVE')} data-testid="btn-archive">
                {lifecycleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />} Archivia
              </Button>
            )}
            {showActionsMenu && lifecycle === 'ARCHIVIATO' && (
              <Button variant="outline" disabled={lifecycleBusy} className="w-full gap-1 border-brand-green text-brand-green hover:bg-brand-green hover:text-white disabled:opacity-50 sm:w-auto" onClick={() => doLifecycle('RESTORE')} data-testid="btn-restore">
                {lifecycleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Ripristina
              </Button>
            )}
            <Link to={`/ferie/${p.ferie.legacy_id}`} data-testid="link-to-ferie" className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl border border-brand-green px-3 py-2 text-sm font-medium text-brand-green transition-colors hover:bg-brand-green hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 sm:w-auto">
              <Palmtree className="w-4 h-4" /> Apri ferie
            </Link>
          </div>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-app-border" role="tablist" aria-label="Sezioni scheda persona">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} role="tab" aria-selected={active} aria-controls={`panel-${t.id}`} tabIndex={active ? 0 : -1} data-testid={`tab-${t.id}`}
              className={`inline-flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 ${
                active ? 'border-brand-green text-brand-navy' : 'border-transparent text-stone-500 hover:text-brand-navy'
              }`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'identita' && <IdentitaPanel p={p} canEdit={canEdit} onSaved={load} F={F} />}

      {tab === 'lavoro' && (
        <div id="panel-lavoro" role="tabpanel" tabIndex="0" className="space-y-4" data-testid="panel-lavoro">
          <div className="rounded-2xl border border-app-border bg-app-surface p-4 sm:p-6">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Assegnazioni ({p.lavoro.assignments_count})</div>
              {canAssign && (
                <Button size="sm" onClick={() => setModal({ kind: 'add' })} className="bg-brand-green hover:bg-brand-green-hover text-white gap-1" data-testid="btn-add-assignment">
                  <Plus className="w-3.5 h-3.5" /> Aggiungi
                </Button>
              )}
            </div>
            {p.lavoro.assignments.length === 0 && <div className="text-sm text-stone-400">Nessuna assegnazione.</div>}
            <div className="space-y-2">
              {p.lavoro.assignments.map((a, i) => (
                <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-app-border p-3" data-testid={`assign-${i}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <MapPin className={`w-4 h-4 flex-shrink-0 ${a.status !== 'ATTIVA' ? 'text-stone-400' : 'text-brand-green'}`} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-brand-navy">
                        {a.company_name} · {a.location_name}
                        {a.is_primary && a.status === 'ATTIVA' && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-brand-green/10 text-brand-green font-semibold">Principale</span>}
                        {a.status === 'CHIUSA' && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-stone-100 text-stone-500 font-semibold">Chiusa</span>}
                        {a.status === 'ANNULLATA' && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">Annullata (errore)</span>}
                      </div>
                      <div className="text-xs text-stone-500">{a.department || '—'} · {a.job_title || '—'}</div>
                      <div className="text-xs text-stone-400">
                        {a.start_date ? `dal ${a.start_date}` : 'Data da completare'}
                        {a.end_date ? ` · al ${a.end_date}` : ''}
                      </div>
                    </div>
                  </div>
                  {canAssign && a.status === 'ATTIVA' && (
                    <div className="flex gap-1 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => setModal({ kind: 'modify', assignment: a })} data-testid={`btn-modify-${i}`} className="text-brand-navy border-stone-300 hover:bg-stone-50">Modifica</Button>
                      <Button size="sm" variant="outline" onClick={() => setModal({ kind: 'transfer', assignment: a })} data-testid={`btn-transfer-${i}`} className="text-brand-blue border-brand-blue/30 hover:bg-brand-blue/5">Trasferisci</Button>
                      <Button size="sm" variant="outline" onClick={() => window.confirm(`Chiudere l'assegnazione a ${a.location_name}?`) && closeAssign(p.legacy_id, a.id, load)} data-testid={`btn-close-${i}`} className="text-red-700 border-red-200 hover:bg-red-50">Chiudi</Button>
                      <Button size="sm" variant="outline" onClick={() => { const r = window.prompt("Motivo dell'annullamento (facoltativo):", ''); if (r !== null) cancelAssign(p.legacy_id, a.id, r, load); }} data-testid={`btn-cancel-${i}`} className="text-red-800 border-red-300 hover:bg-red-100">Annulla (errore)</Button>
                    </div>
                  )}
                  {canAssign && a.status === 'CHIUSA' && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => { const r = window.prompt("Motivo dell'annullamento (facoltativo):", ''); if (r !== null) cancelAssign(p.legacy_id, a.id, r, load); }} data-testid={`btn-cancel-${i}`} className="text-red-800 border-red-300 hover:bg-red-100">Annulla (errore)</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'contratto' && <ContrattoPanel legacyId={p.legacy_id} canManage={hasCap(me, 'personale.contract.manage')} />}

      {tab === 'documenti' && <DocumentsTab legacyId={p.legacy_id} />}

      {tab === 'ferie' && (
        <div id="panel-ferie" role="tabpanel" tabIndex="0" className="space-y-3 rounded-2xl border border-app-border bg-app-surface p-4 sm:p-6" data-testid="panel-ferie">
          <div className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Ferie e assenze</div>
          {p.ferie?.policy && (
            (() => {
              const isEmployeeOverride = p.ferie.policy.source === 'employee' && p.ferie.policy.active !== false;
              return (
            <div className="rounded-xl border border-stone-200 bg-stone-50 p-4" data-testid="employee-leave-policy">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-brand-green">
                    {isEmployeeOverride ? 'Policy individuale attiva' : 'Policy effettiva'}
                  </div>
                  <h3 className="mt-1 font-semibold text-brand-navy">{p.ferie.policy.nomeConfigurazione}</h3>
                  <p className="mt-1 text-sm text-stone-500">{p.ferie.policy.fonte}</p>
                </div>
                <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-stone-600 ring-1 ring-stone-200">
                  {p.ferie.policy.source === 'legacy' ? 'Fallback legacy' : `Fonte ${p.ferie.policy.source}`}
                </span>
              </div>
              <div className="mt-4 grid gap-3 text-sm sm:grid-cols-5">
                <div><div className="text-xs uppercase tracking-wide text-stone-400">Giorni annui</div><div className="mt-1 font-semibold text-brand-navy">{p.ferie.policy.giorniAnnui ?? '—'}</div></div>
                <div><div className="text-xs uppercase tracking-wide text-stone-400">Conteggio</div><div className="mt-1 font-semibold text-brand-navy">{p.ferie.policy.criterioConteggio || '—'}</div></div>
                <div><div className="text-xs uppercase tracking-wide text-stone-400">Valida dal</div><div className="mt-1 font-semibold text-brand-navy">{p.ferie.policy.dataInizioValidita || 'Legacy'}</div></div>
                <div><div className="text-xs uppercase tracking-wide text-stone-400">Valida fino al</div><div className="mt-1 font-semibold text-brand-navy">{p.ferie.policy.dataFineValidita || 'Senza scadenza'}</div></div>
                <div><div className="text-xs uppercase tracking-wide text-stone-400">Stato</div><div className="mt-1 font-semibold text-brand-navy">{isEmployeeOverride ? 'Attiva' : 'Applicata'}</div></div>
              </div>
              {isEmployeeOverride && canManagePolicy && (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-4 gap-2 border-red-200 text-red-700 hover:bg-red-50"
                  onClick={deactivateEmployeeOverride}
                  disabled={policySaving}
                  data-testid="btn-deactivate-employee-leave-override"
                >
                  <XCircle className="h-4 w-4" /> Disattiva override
                </Button>
              )}
            </div>
              );
            })()
          )}
          <p className="text-sm text-stone-600">Il saldo e le richieste ferie sono gestiti nel modulo dedicato.</p>
          {canManagePolicy && (
            <Button
              type="button"
              variant="outline"
              className="w-fit gap-2 border-brand-green/30 text-brand-green hover:bg-brand-green/5"
              onClick={() => {
                setPolicyDraft(newPolicyDraft(p.ferie?.policy));
                setPolicyEditorOpen((current) => !current);
              }}
              data-testid="btn-edit-employee-leave-policy"
            >
              <Edit2 className="h-4 w-4" /> Nuova policy individuale
            </Button>
          )}
          {policyEditorOpen && canManagePolicy && (
            <form onSubmit={saveEmployeePolicy} className="rounded-xl border border-brand-green/25 bg-white p-4" data-testid="employee-leave-policy-form">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5"><Label htmlFor="employee-policy-name">Nome configurazione</Label><Input id="employee-policy-name" value={policyDraft.nome_configurazione} onChange={(event) => setPolicyDraft((current) => ({ ...current, nome_configurazione: event.target.value }))} required /></div>
                <div className="space-y-1.5"><Label htmlFor="employee-policy-source">Fonte documentale</Label><Input id="employee-policy-source" value={policyDraft.fonte} onChange={(event) => setPolicyDraft((current) => ({ ...current, fonte: event.target.value }))} placeholder="Contratto o accordo approvato" required /></div>
                <div className="space-y-1.5"><Label htmlFor="employee-policy-source-type">Tipo fonte</Label><select id="employee-policy-source-type" value={policyDraft.tipo_fonte} onChange={(event) => setPolicyDraft((current) => ({ ...current, tipo_fonte: event.target.value }))} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">{POLICY_SOURCE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
                <div className="space-y-1.5"><Label htmlFor="employee-policy-days">Giorni annui</Label><Input id="employee-policy-days" type="number" min="0.01" max="366" step="0.01" value={policyDraft.giorni_annui} onChange={(event) => setPolicyDraft((current) => ({ ...current, giorni_annui: event.target.value }))} required /></div>
                <div className="space-y-1.5"><Label htmlFor="employee-policy-counting">Criterio di conteggio</Label><select id="employee-policy-counting" value={policyDraft.criterio_conteggio} onChange={(event) => setPolicyDraft((current) => ({ ...current, criterio_conteggio: event.target.value }))} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"><option value="5_GIORNI">Settimana di 5 giorni</option><option value="6_GIORNI">Settimana di 6 giorni</option><option value="CALENDARIO">Giorni di calendario</option><option value="PERSONALIZZATO">Personalizzato</option></select></div>
                <div className="space-y-1.5"><Label htmlFor="employee-policy-start">Valida dal</Label><Input id="employee-policy-start" type="date" value={policyDraft.data_inizio_validita} onChange={(event) => setPolicyDraft((current) => ({ ...current, data_inizio_validita: event.target.value }))} required /></div>
                <div className="space-y-1.5"><Label htmlFor="employee-policy-ccnl">CCNL / codice</Label><Input id="employee-policy-ccnl" value={policyDraft.ccnl} onChange={(event) => setPolicyDraft((current) => ({ ...current, ccnl: event.target.value }))} /></div>
                <div className="space-y-1.5"><Label htmlFor="employee-policy-code">Codice CCNL</Label><Input id="employee-policy-code" value={policyDraft.codice_ccnl} onChange={(event) => setPolicyDraft((current) => ({ ...current, codice_ccnl: event.target.value }))} /></div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button type="submit" disabled={policySaving} className="bg-brand-green text-white hover:bg-brand-green-hover">{policySaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva policy</Button>
                <Button type="button" variant="outline" onClick={() => setPolicyEditorOpen(false)} disabled={policySaving}>Annulla</Button>
              </div>
            </form>
          )}
          <Button asChild className="bg-brand-green hover:bg-brand-green-hover text-white gap-2 w-fit">
            <Link to={`/ferie/${p.ferie.legacy_id}`} data-testid="btn-open-ferie">
              <Palmtree className="w-4 h-4" /> Apri modulo Ferie <ArrowRight className="w-3 h-3" />
            </Link>
          </Button>
        </div>
      )}

      {tab === 'accesso' && (
        <AccessoTab p={p} onChanged={load} />
      )}

      {tab === 'timeline' && <TimelinePanel legacyId={p.legacy_id} />}

      {modal?.kind === 'add' && <AssignmentModal mode="add" personId={p.legacy_id} companyId={p.lavoro.company_id} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal?.kind === 'transfer' && <AssignmentModal mode="transfer" personId={p.legacy_id} companyId={p.lavoro.company_id} source={modal.assignment} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal?.kind === 'modify' && <AssignmentModal mode="modify" personId={p.legacy_id} companyId={p.lavoro.company_id} source={modal.assignment} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
    </div>
  );
}

async function closeAssign(legacy, aid, refresh) {
  try {
    await apiFetch(`/api/personale/${legacy}/assignments/${aid}/close`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ end_date: new Date().toISOString().slice(0, 10) }),
    });
    toast.success('Assegnazione chiusa'); refresh();
  } catch (e) { toast.error(e.message); }
}

async function cancelAssign(legacy, aid, reason, refresh) {
  try {
    await apiFetch(`/api/personale/${legacy}/assignments/${aid}/cancel`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason || null }),
    });
    toast.success('Assegnazione annullata'); refresh();
  } catch (e) { toast.error(e.message); }
}

function IdentitaPanel({ p, canEdit, onSaved, F }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (editing) {
      setForm({
        display_name_override: p.identity.display_name || '',
        first_name: p.identity.first_name || '',
        last_name: p.identity.last_name || '',
        email_personal: p.identity.email_personal || '',
        phone: p.identity.phone || '',
        codice_interno: p.identity.codice_interno || '',
      });
    }
  }, [editing, p]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      Object.keys(form).forEach((k) => { payload[k] = form[k] || null; });
      await apiFetch(`/api/personale/${p.legacy_id}/identity`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      toast.success('Identità aggiornata');
      setEditing(false); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div id="panel-identita" role="tabpanel" tabIndex="0" className="rounded-2xl border border-app-border bg-app-surface p-4 sm:p-6" data-testid="panel-identita">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Dati anagrafici</div>
        {canEdit && !editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="gap-1 text-brand-green border-brand-green" data-testid="btn-edit-identity">
            <Edit2 className="w-3.5 h-3.5" /> Modifica
          </Button>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="gap-1"><X className="w-3.5 h-3.5" /> Annulla</Button>
            <Button size="sm" onClick={save} disabled={busy} className="bg-brand-green hover:bg-brand-green-hover text-white gap-1" data-testid="btn-save-identity">
              <Save className="w-3.5 h-3.5" /> {busy ? 'Salvo…' : 'Salva'}
            </Button>
          </div>
        )}
      </div>
      {!editing ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
          <F label="Nome visualizzato" value={p.identity.display_name} testid="f-display-name" />
          <F label="Codice RestaurantOS" value={p.identity.codice_ros} testid="f-codice-ros" />
          <F label="Nome" value={p.identity.first_name} testid="f-first-name" />
          <F label="Cognome" value={p.identity.last_name} testid="f-last-name" />
          <F label="Email personale" value={p.identity.email_personal} testid="f-email" />
          <F label="Telefono" value={p.identity.phone} testid="f-phone" />
          <F label="Codice interno" value={p.identity.codice_interno} testid="f-codice-interno" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Nome visualizzato</Label><Input value={form.display_name_override} onChange={(e) => set('display_name_override', e.target.value)} data-testid="i-display-name" /></div>
          <div><Label>Codice RestaurantOS</Label><Input value={p.identity.codice_ros} disabled className="bg-stone-50" /></div>
          <div><Label>Nome</Label><Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} data-testid="i-first-name" /></div>
          <div><Label>Cognome</Label><Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} data-testid="i-last-name" /></div>
          <div><Label>Email personale</Label><Input value={form.email_personal} onChange={(e) => set('email_personal', e.target.value)} data-testid="i-email" /></div>
          <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="i-phone" /></div>
          <div><Label>Codice interno</Label><Input value={form.codice_interno} onChange={(e) => set('codice_interno', e.target.value)} data-testid="i-codice-interno" /></div>
        </div>
      )}
    </div>
  );
}

const EVENT_LABEL = {
  created: 'Persona creata',
  identity_edited: 'Identità modificata',
  assignment_added: 'Assegnazione aggiunta',
  assignment_modified: 'Assegnazione modificata',
  assignment_transferred: 'Trasferimento',
  assignment_closed: 'Assegnazione conclusa',
  assignment_cancelled: 'Assegnazione annullata (errore)',
  document_uploaded: 'Documento caricato',
  document_archived: 'Documento archiviato',
  lifecycle_changed: 'Cambio stato',
};

function TimelinePanel({ legacyId }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    (async () => {
      try { setEvents(await apiFetch(`/api/personale/${legacyId}/events`)); } catch { setEvents([]); }
    })();
  }, [legacyId]);
  if (events === null) return <div className="flex items-center gap-2 text-stone-500"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;

  const summarize = (e) => {
    if (e.event_type === 'lifecycle_changed' && e.after?.action) {
      const map = { SUSPEND: 'Sospesa', REACTIVATE: 'Riattivata', CESSATE: 'Cessata', ARCHIVE: 'Archiviata', RESTORE: 'Ripristinata' };
      return `→ ${map[e.after.action] || e.after.lifecycle_status || ''}`;
    }
    if (e.event_type === 'identity_edited' && e.after) {
      const keys = Object.keys(e.after).slice(0, 3);
      return `Campi modificati: ${keys.join(', ')}`;
    }
    if (e.event_type === 'assignment_transferred' && e.after) {
      return `Trasferita al nuovo locale (decorrenza ${e.after.effective_date || '—'})`;
    }
    return null;
  };

  return (
    <div id="panel-timeline" role="tabpanel" tabIndex="0" className="rounded-2xl border border-app-border bg-app-surface p-4 sm:p-6" data-testid="panel-timeline">
      <div className="text-xs uppercase tracking-wider text-stone-400 font-semibold mb-4">Storia della persona</div>
      {events.length === 0 && <div className="text-sm text-stone-400">Nessun evento registrato.</div>}
      <ol className="space-y-3">
        {events.map((e) => {
          const summary = summarize(e);
          return (
            <li key={e.id} data-testid={`event-${e.event_type}`} className="flex gap-3 border-l-2 border-brand-green pl-3">
              <div>
                <div className="text-sm font-medium text-brand-navy">{EVENT_LABEL[e.event_type] || e.event_type.replace(/_/g, ' ')}</div>
                <div className="text-xs text-stone-500">{new Date(e.occurred_at).toLocaleString('it-IT')} · {e.actor || '—'}</div>
                {summary && <div className="text-xs text-stone-600 mt-0.5">{summary}</div>}
                {e.reason && <div className="text-xs text-stone-600 italic mt-0.5">Motivo: {e.reason}</div>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ContrattoPanel({ legacyId, canManage }) {
  const [contracts, setContracts] = React.useState(null);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({ tipo: 'Tempo indeterminato', tempo_lavoro: 'Full-time', ore_settimanali: '', mansione: '', livello: '', ccnl: '', valid_from: '', note: '' });
  const [busy, setBusy] = React.useState(false);
  const load = async () => { try { setContracts(await apiFetch(`/api/personale/${legacyId}/contracts`)); } catch { setContracts([]); } };
  // load is intentionally scoped to the selected person.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { load(); }, [legacyId]);
  const submit = async () => {
    if (busy) return; setBusy(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v != null) payload[k] = k === 'ore_settimanali' ? Number(v) : v; });
      await apiFetch(`/api/personale/${legacyId}/contracts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      toast.success('Contratto creato');
      setShowForm(false);
      setForm({ tipo: 'Tempo indeterminato', tempo_lavoro: 'Full-time', ore_settimanali: '', mansione: '', livello: '', ccnl: '', valid_from: '', note: '' });
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const closeCtr = async (cid) => {
    if (!window.confirm('Chiudere il contratto?')) return;
    try {
      await apiFetch(`/api/personale/${legacyId}/contracts/${cid}/close`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ valid_to: new Date().toISOString().slice(0, 10) }) });
      toast.success('Contratto chiuso'); load();
    } catch (e) { toast.error(e.message); }
  };
  if (contracts === null) return <div className="flex items-center gap-2 text-stone-500"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;
  return (
    <div id="panel-contratto" role="tabpanel" tabIndex="0" className="space-y-4" data-testid="panel-contratto">
      <div className="rounded-2xl border border-app-border bg-app-surface p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Contratti ({contracts.length})</div>
          {canManage && !showForm && (
            <Button size="sm" onClick={() => setShowForm(true)} className="bg-brand-green hover:bg-brand-green-hover text-white gap-1" data-testid="btn-add-contract"><Plus className="w-3.5 h-3.5" /> Aggiungi contratto</Button>
          )}
        </div>
        {contracts.length === 0 && !showForm && (
          <div className="text-center py-6 text-stone-500 text-sm" data-testid="contract-empty">
            <div className="font-medium text-brand-navy">Nessun contratto inserito</div>
            <div className="mt-1">Aggiungi il primo contratto per questa persona.</div>
          </div>
        )}
        {showForm && (
          <div className="mb-4 grid grid-cols-1 gap-3 border-b border-app-border pb-4 md:grid-cols-2">
            <div><Label htmlFor="ct-tipo">Tipo *</Label><select id="ct-tipo" value={form.tipo} onChange={(e) => setForm({...form, tipo: e.target.value})} data-testid="ct-tipo" className="h-10 w-full rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm text-app-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200"><option>Tempo indeterminato</option><option>Tempo determinato</option><option>Apprendistato</option><option>Intermittente / A chiamata</option></select></div>
            <div><Label htmlFor="ct-tempo">Tempo di lavoro</Label><select id="ct-tempo" value={form.tempo_lavoro} onChange={(e) => setForm({...form, tempo_lavoro: e.target.value})} data-testid="ct-tempo" className="h-10 w-full rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm text-app-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200"><option>Full-time</option><option>Part-time</option></select></div>
            <div><Label>Ore settimanali</Label><Input type="number" step="0.5" value={form.ore_settimanali} onChange={(e) => setForm({...form, ore_settimanali: e.target.value})} data-testid="ct-ore" /></div>
            <div><Label>Mansione</Label><Input value={form.mansione} onChange={(e) => setForm({...form, mansione: e.target.value})} data-testid="ct-mansione" placeholder="es. Cuoco" /></div>
            <div><Label>Livello</Label><Input value={form.livello} onChange={(e) => setForm({...form, livello: e.target.value})} data-testid="ct-livello" /></div>
            <div><Label>CCNL</Label><Input value={form.ccnl} onChange={(e) => setForm({...form, ccnl: e.target.value})} data-testid="ct-ccnl" placeholder="es. Pubblici Esercizi" /></div>
            <div><Label>Valido dal</Label><Input type="date" value={form.valid_from} onChange={(e) => setForm({...form, valid_from: e.target.value})} data-testid="ct-valid-from" /></div>
            <div className="md:col-span-2"><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} data-testid="ct-note" /></div>
            <div className="md:col-span-2 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button onClick={submit} disabled={busy} className="bg-brand-green hover:bg-brand-green-hover text-white gap-1" data-testid="ct-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva contratto</Button>
            </div>
          </div>
        )}
        <div className="space-y-2">
          {contracts.map((c, i) => (
            <div key={c.id} data-testid={`contract-${i}`} className="rounded-xl border border-app-border p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-brand-navy">
                  {c.tipo}
                  <span className={`ml-2 text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full text-white ${c.status === 'ATTIVO' ? 'bg-brand-green' : c.status === 'CHIUSO' ? 'bg-stone-400' : 'bg-amber-500'}`}>{c.status}</span>
                </div>
                {canManage && c.status === 'ATTIVO' && (
                  <Button size="sm" variant="outline" onClick={() => closeCtr(c.id)} className="text-red-700 border-red-200" data-testid={`btn-close-contract-${i}`}>Chiudi</Button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-stone-600">
                <div>{c.tempo_lavoro || '—'}</div>
                <div>{c.ore_settimanali != null ? `${c.ore_settimanali}h/sett` : '—'}</div>
                <div>{c.mansione || '—'}</div>
                <div>{c.ccnl || '—'}</div>
                <div className="col-span-2 md:col-span-4 text-xs text-stone-400">Dal {c.valid_from || '—'}{c.valid_to ? ` al ${c.valid_to}` : ''}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AssignmentModal({ mode, personId, companyId, source, onClose, onDone }) {
  // Hierarchical state: Azienda → Locale → Reparto → Mansione
  const [form, setForm] = useState({
    company_id: mode === 'modify' ? (source?.company_id || '') : (mode === 'add' ? (companyId || '') : ''),
    location_id: mode === 'modify' ? (source?.location_id || '') : '',
    department_id: mode === 'modify' ? (source?.department_id || '') : '',
    mansione: source?.job_title || '',
    effective_date: mode === 'modify' ? (source?.start_date || new Date().toISOString().slice(0, 10)) : new Date().toISOString().slice(0, 10),
    is_primary: mode === 'modify' ? !!source?.is_primary : false,
  });
  const [busy, setBusy] = useState(false);
  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const submit = async () => {
    setBusy(true);
    try {
      if (mode === 'add') {
        await apiFetch(`/api/personale/${personId}/assignments`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ location_id: form.location_id, department_id: form.department_id || null, job_title: form.mansione || null, is_primary: form.is_primary, start_date: form.effective_date || null }),
        });
      } else if (mode === 'modify') {
        const payload = {
          department_id: form.department_id || null,
          job_title: form.mansione || null,
          is_primary: form.is_primary,
        };
        if (form.effective_date) payload.start_date = form.effective_date;
        await apiFetch(`/api/personale/${personId}/assignments/${source.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch(`/api/personale/${personId}/assignments/transfer`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source_assignment_id: source.id, target_location_id: form.location_id, target_department_id: form.department_id || null, job_title: form.mansione || null, effective_date: form.effective_date || null }),
        });
      }
      toast.success(mode === 'add' ? 'Assegnazione creata' : mode === 'modify' ? 'Assegnazione aggiornata' : 'Trasferimento completato');
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const titleByMode = { add: 'Aggiungi assegnazione', transfer: 'Trasferisci assegnazione', modify: 'Modifica assegnazione' };
  const canSubmit = !busy && form.company_id && form.location_id && form.mansione &&
                    (mode === 'modify' || form.effective_date);
  const lockLevels = mode === 'modify' ? ['company', 'location'] : [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid={`assign-modal-${mode}`} role="dialog" aria-modal="true" aria-labelledby={`assign-modal-title-${mode}`} onClick={onClose} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-app-border bg-app-surface p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div id={`assign-modal-title-${mode}`} className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green">{titleByMode[mode] || mode}</div>
            {source && mode !== 'add' && (
              <div className="text-xs text-stone-500 mt-1">
                {mode === 'transfer' ? 'Da: ' : ''}
                <span className="font-medium text-brand-navy">{source.company_name || 'Dato non disponibile'} · {source.location_name || 'Dato non disponibile'}</span>
                {source.department && ` · ${source.department}`}
                {source.job_title && ` · ${source.job_title}`}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Chiudi assegnazione" data-testid="btn-close-modal" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-stone-400 hover:bg-stone-50 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <AssignmentScopeSelector
            value={form}
            onChange={patch}
            lockLevels={lockLevels}
            testidPrefix="a"
          />
          <div>
            <Label>{mode === 'modify' ? 'Data decorrenza (start_date)' : 'Data decorrenza *'}</Label>
            <Input type="date" value={form.effective_date} onChange={(e) => patch({ effective_date: e.target.value })} data-testid="a-date" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_primary} onChange={(e) => patch({ is_primary: e.target.checked })} data-testid="a-primary" />
            Assegnazione principale <span className="text-stone-400">(facoltativa)</span>
          </label>
          <Button onClick={submit} disabled={!canSubmit} className="w-full bg-brand-green hover:bg-brand-green-hover text-white" data-testid="a-submit">
            {busy ? 'Salvo…' : (mode === 'add' ? 'Aggiungi' : mode === 'modify' ? 'Salva modifiche' : 'Conferma trasferimento')}
          </Button>
          {mode === 'modify' && (
            <p className="text-[11px] text-stone-500">Per cambiare <b>Azienda</b> o <b>Locale</b> usa <b>Trasferisci</b>: viene chiusa la sorgente e aperta la nuova assegnazione, preservando lo storico.</p>
          )}
        </div>
      </div>
    </div>
  );
}
